/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import React from 'react';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';

interface StickyHeaderBarProps {
  children: React.ReactNode;
}

/**
 * Wraps the property listing control bar (title, search, filters) so that it
 * stays pinned to the top of the scroll container on mobile while the cards
 * scroll beneath it. On desktop the bar keeps its original non-scrolling
 * `shrink-0` treatment, preserving the existing layout.
 *
 * @param {StickyHeaderBarProps} props - Component props.
 * @param {React.ReactNode} props.children - The control bar content to wrap.
 * @returns {JSX.Element} The wrapped, conditionally-sticky control bar.
 */
export const StickyHeaderBar: React.FC<StickyHeaderBarProps> = ({ children }) => {
  const isMobile = useIsMobile();

  const className = isMobile
    ? 'sticky top-0 z-10 bg-(--surface-primary) pb-(--space-4)'
    : 'shrink-0 pb-(--space-4)';

  return (
    <div data-testid="sticky-header-bar" className={className}>
      {children}
    </div>
  );
};
